import React, { useState, useEffect, useRef } from 'react';
import PageExplainer from '@/components/PageExplainer';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Camera, ImagePlus, User, Briefcase, Handshake, Settings as SettingsIcon } from 'lucide-react';
import { toast } from 'sonner';
import XPProfileCard from '@/components/XPProfileCard';
import ProfileTab from '@/components/profile/ProfileTab';
import ExpertiseTab from '@/components/profile/ExpertiseTab';
import NetworkingTab from '@/components/profile/NetworkingTab';
import SettingsTab from '@/components/profile/SettingsTab';
import HeadshotCropper from '@/components/HeadshotCropper';

const BACKEND_URL = '';

export default function ProfilePage({ user }) {
  const photoInputRef = useRef(null);
  const headerInputRef = useRef(null);
  const securityRef = useRef(null);
  const [searchParams] = useSearchParams();
  const [uploading, setUploading] = useState(false);
  const [uploadingHeader, setUploadingHeader] = useState(false);
  const [profilePicture, setProfilePicture] = useState(user?.picture || null);
  const [headerImage, setHeaderImage] = useState(user?.profile?.header_image || null);
  const [pendingPhoto, setPendingPhoto] = useState(null); // raw File awaiting crop
  const isNewUser = !user?.profile?.profile_completed;
  const [profile, setProfile] = useState({
    name: user?.name || '',
    role: user?.profile?.role || '',
    organisation: user?.profile?.organisation || '',
    sector: user?.profile?.sector || '',
    location: user?.profile?.location || user?.profile?.city || '',
    country: user?.profile?.country || '',
    postcode: user?.profile?.postcode || '',
    region: user?.profile?.region || '',
    gender: user?.profile?.gender || '',
    age_group: user?.profile?.age_group || '',
    background: user?.profile?.background || '',
    specialist_areas: user?.profile?.specialist_areas || [],
    linkedin_url: user?.profile?.linkedin_url || '',
    bio: user?.profile?.bio || '',
    working_on: user?.profile?.working_on || '',
    expertise: user?.profile?.expertise || [],
    challenges: user?.profile?.challenges || [],
    interests: user?.profile?.interests || [],
    looking_for: user?.profile?.looking_for || [],
    projects: user?.profile?.projects || [],
    certifications: user?.profile?.certifications || [],
    sustainability_goals: user?.profile?.sustainability_goals || [],
    years_experience: user?.profile?.years_experience || '',
    priorities_2026: user?.profile?.priorities_2026 || [],
    community_goals: user?.profile?.community_goals || [],
    self_description: user?.profile?.self_description || '',
    self_descriptions: user?.profile?.self_descriptions
      || (user?.profile?.self_description ? [user.profile.self_description] : []),
    britelab_consent: user?.profile?.britelab_consent || false,
    forum_interests: user?.profile?.forum_interests || [],
    forum_topics_of_interest: user?.profile?.forum_topics_of_interest || [],
    mobile_number: user?.profile?.mobile_number || '',
    qualifications: user?.profile?.qualifications || [],
    header_image: user?.profile?.header_image || null,
    behavioural_decisions: user?.profile?.behavioural_decisions || '',
    behavioural_processing: user?.profile?.behavioural_processing || '',
    behavioural_working: user?.profile?.behavioural_working || '',
    behavioural_collaboration: user?.profile?.behavioural_collaboration || '',
  });

  const defaults = {
    new_messages: true, matched_news: true, new_events: true, new_courses: true,
    new_members: true, smart_match_joins: true,
    forum_comments: true, forum_new_posts: true,
    event_reminders: true, britelab_invites: true,
    weekly_digest_emails: true,
    meeting_requests: true,
  };
  const [notifPrefs, setNotifPrefs] = useState({ ...defaults, ...(user?.profile?.notification_preferences || {}) });
  const privacyDefaults = { show_email: false, show_phone: false, show_linkedin: true };
  const [privacyPrefs, setPrivacyPrefs] = useState({ ...privacyDefaults, ...(user?.privacy || {}) });
  const [companyId, setCompanyId] = useState(user?.company_id || null);
  const [companyName, setCompanyName] = useState('');

  const token = sessionStorage.getItem('session_token');
  const authHeaders = token ? { 'Authorization': `Bearer ${token}` } : {};
  const navigate = useNavigate();

  useEffect(() => {
    if (user?.company_id && !companyName) {
      fetch(`${BACKEND_URL}/api/companies/${user.company_id}`, {
        credentials: 'include', headers: authHeaders,
      }).then(r => r.ok ? r.json() : null).then(d => { if (d) setCompanyName(d.name); }).catch(() => {});
    }
  }, [user?.company_id]);

  useEffect(() => {
    if (searchParams.get('section') === 'security' && securityRef.current) {
      setTimeout(() => securityRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300);
    }
  }, [searchParams]);

  const handleSave = async () => {
    // Validate first-page mandatory fields
    const missing = [];
    if (!profile.name?.trim()) missing.push('Full Name');
    if (!profile.role?.trim()) missing.push('Job Title');
    if (!profile.organisation?.trim()) missing.push('Organisation');
    if (!profile.background) missing.push('Stakeholder Type');
    if (!profile.country) missing.push('Country');
    if (!profile.location?.trim()) missing.push('City');
    if (!profile.sector) missing.push('Sector');
    if (!Array.isArray(profile.self_descriptions) || profile.self_descriptions.length === 0) missing.push('How Would You Describe Yourself');
    if (!profile.bio?.trim()) missing.push('About Me');
    if (!profile.behavioural_decisions || !profile.behavioural_processing || !profile.behavioural_working || !profile.behavioural_collaboration) missing.push('Behavioural Profile');
    if (missing.length) {
      toast.error(`Please complete: ${missing.join(', ')}`);
      return;
    }
    try {
      const response = await fetch(`${BACKEND_URL}/api/members/me`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        credentials: 'include',
        body: JSON.stringify({
          name: profile.name,
          company_id: companyId || null,
          privacy: privacyPrefs,
          profile: {
            role: profile.role, organisation: profile.organisation, sector: profile.sector,
            location: profile.location, country: profile.country,
            postcode: profile.postcode,
            region: profile.region, gender: profile.gender,
            age_group: profile.age_group, background: profile.background,
            specialist_areas: profile.specialist_areas, linkedin_url: profile.linkedin_url,
            bio: profile.bio, working_on: profile.working_on, expertise: profile.expertise,
            challenges: profile.challenges, interests: profile.interests,
            looking_for: profile.looking_for, projects: profile.projects,
            certifications: profile.certifications, sustainability_goals: profile.sustainability_goals,
            years_experience: profile.years_experience || null,
            priorities_2026: profile.priorities_2026, community_goals: profile.community_goals,
            self_description: profile.self_descriptions?.[0] || profile.self_description || '',
            self_descriptions: profile.self_descriptions || [],
            mobile_number: profile.mobile_number || null,
            qualifications: profile.qualifications,
            behavioural_decisions: profile.behavioural_decisions,
            behavioural_processing: profile.behavioural_processing,
            behavioural_working: profile.behavioural_working,
            behavioural_collaboration: profile.behavioural_collaboration,
            profile_completed: true, notification_preferences: notifPrefs,
            britelab_consent: profile.britelab_consent,
            forum_interests: profile.forum_interests,
            forum_topics_of_interest: profile.forum_topics_of_interest,
          }
        })
      });
      if (!response.ok) throw new Error('Update failed');
      const updatedUser = await response.json();
      // Update cached user in storage so ProfileCompletionBanner and other components see fresh data
      const freshUser = { ...user, ...updatedUser };
      const freshStr = JSON.stringify(freshUser);
      sessionStorage.setItem('sll_user', freshStr);
      localStorage.setItem('sll_user', freshStr);
      // Dispatch storage event so ProtectedRoute picks up the updated user
      window.dispatchEvent(new Event('user-updated'));
      toast.success(isNewUser ? 'Profile completed! Welcome to SLL.' : 'Profile updated successfully!');
      if (isNewUser) navigate('/progress');
    } catch {
      toast.error('Failed to update profile');
    }
  };

  const handleUpload = async (event, type) => {
    const file = event.target.files?.[0];
    // Reset the input so the same file can be re-picked later
    if (event.target) event.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please select an image file'); return; }

    // Profile photo: open the cropper instead of uploading directly
    if (type === 'photo') {
      if (file.size > 8 * 1024 * 1024) { toast.error('Image must be under 8MB'); return; }
      setPendingPhoto(file);
      return;
    }

    // Header image: keep the original direct-upload flow
    if (file.size > 500 * 1024) { toast.error('Image must be under 500KB. Please resize your image before uploading.'); return; }
    setUploadingHeader(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await fetch(`${BACKEND_URL}/api/members/me/upload-header`, {
        method: 'POST', credentials: 'include', headers: authHeaders, body: formData
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Upload failed');
      }
      const data = await response.json();
      const bustedHeader = data.header_image && data.header_image.startsWith('/')
        ? `${data.header_image}${data.header_image.includes('?') ? '&' : '?'}v=${Date.now()}`
        : data.header_image;
      setHeaderImage(bustedHeader);
      try {
        const cached = JSON.parse(sessionStorage.getItem('sll_user') || 'null');
        if (cached) {
          cached.profile = { ...(cached.profile || {}), header_image: bustedHeader };
          sessionStorage.setItem('sll_user', JSON.stringify(cached));
          window.dispatchEvent(new Event('user-updated'));
        }
      } catch { /* ignore */ }
      toast.success('Header image updated!');
    } catch (e) {
      toast.error(e.message || 'Failed to upload image');
    } finally {
      setUploadingHeader(false);
    }
  };

  const handleCroppedSave = async (blob) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', blob, 'headshot.jpg');
      const response = await fetch(`${BACKEND_URL}/api/members/me/upload-photo`, {
        method: 'POST', credentials: 'include', headers: authHeaders, body: formData
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Upload failed');
      }
      const data = await response.json();
      // Cache-bust so cached <img> tags refresh
      const bustedUrl = data.picture && data.picture.startsWith('/')
        ? `${data.picture}${data.picture.includes('?') ? '&' : '?'}v=${Date.now()}`
        : data.picture;
      setProfilePicture(bustedUrl);
      // Propagate to global user state so Navigation, dashboards, member tiles, etc. update
      try {
        const cached = JSON.parse(sessionStorage.getItem('sll_user') || 'null');
        if (cached) {
          cached.picture = bustedUrl;
          sessionStorage.setItem('sll_user', JSON.stringify(cached));
          window.dispatchEvent(new Event('user-updated'));
        }
      } catch { /* ignore */ }
      toast.success('Profile picture updated!');
      setPendingPhoto(null);
    } catch (e) {
      toast.error(e.message || 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const set = (key, value) => setProfile(p => ({ ...p, [key]: value }));

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation user={user} />
      <main className="pt-24 pb-12 px-4 sm:px-6">
        <div className="container mx-auto max-w-7xl">
          {/* Header banner + photo */}
          <div className="relative mb-20">
            <div className="rounded-xl overflow-hidden">
              <div className="h-48 bg-gradient-to-r from-brand-dark to-brand-sage relative cursor-pointer group"
                onClick={() => headerInputRef.current?.click()}>
                {headerImage ? (
                  <img src={headerImage} alt="Header" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-r from-brand-dark via-brand-navy to-brand-sage">
                    <div className="text-white/40 text-center">
                      <ImagePlus className="h-8 w-8 mx-auto mb-1" />
                      <span className="text-sm">Add header image</span>
                    </div>
                  </div>
                )}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity text-white text-sm font-medium">
                    {uploadingHeader ? 'Uploading...' : 'Change header'}
                  </div>
                </div>
              </div>
            </div>
            <input ref={headerInputRef} type="file" accept="image/*" onChange={(e) => handleUpload(e, 'header')} className="hidden" />

            <div className="absolute -bottom-16 left-8">
              <div className="relative">
                {profilePicture ? (
                  <img src={profilePicture} alt={profile.name} className="w-32 h-32 rounded-full object-cover border-4 border-white shadow-lg" />
                ) : (
                  <div className="w-32 h-32 rounded-full bg-gradient-to-r from-brand-navy to-brand-sage flex items-center justify-center text-white text-4xl font-bold border-4 border-white shadow-lg">
                    {profile.name?.charAt(0) || 'U'}
                  </div>
                )}
                <button data-testid="upload-photo-button" onClick={() => photoInputRef.current?.click()} disabled={uploading}
                  className="absolute bottom-0 right-0 bg-brand-navy hover:bg-brand-dark text-white rounded-full p-3 shadow-lg transition-colors disabled:opacity-50">
                  {uploading ? <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" /> : <Camera className="h-5 w-5" />}
                </button>
              </div>
              <input ref={photoInputRef} type="file" accept="image/*" onChange={(e) => handleUpload(e, 'photo')} className="hidden" />
            </div>
          </div>

          <div className="mb-6 flex items-center justify-between">
            <XPProfileCard />
            <PageExplainer pageKey="profile" label="About your profile" />
          </div>

          {isNewUser && (
            <div data-testid="complete-profile-banner" className="mb-6 p-5 rounded-xl bg-gradient-to-r from-brand-navy/5 to-brand-sage/10 border border-brand-blue/30">
              <h2 className="text-lg font-bold font-heading text-slate-800 mb-1">Welcome! Complete your profile</h2>
              <p className="text-sm text-slate-600">Fill in your details below so we can connect you with the right people and content.</p>
            </div>
          )}

          <Tabs defaultValue={searchParams.get('tab') === 'settings' || searchParams.get('section') === 'security' ? 'settings' : 'profile'} className="w-full">
            <TabsList className="grid w-full grid-cols-4 mb-6">
              <TabsTrigger data-testid="tab-profile" value="profile"><User className="h-3.5 w-3.5 mr-1.5" />Profile</TabsTrigger>
              <TabsTrigger data-testid="tab-expertise" value="expertise"><Briefcase className="h-3.5 w-3.5 mr-1.5" />Expertise</TabsTrigger>
              <TabsTrigger data-testid="tab-networking" value="networking"><Handshake className="h-3.5 w-3.5 mr-1.5" />Networking</TabsTrigger>
              <TabsTrigger data-testid="tab-settings" value="settings"><SettingsIcon className="h-3.5 w-3.5 mr-1.5" />Settings</TabsTrigger>
            </TabsList>

            <TabsContent value="profile">
              <ProfileTab user={user} profile={profile} set={set} handleSave={handleSave} isNewUser={isNewUser}
                companyId={companyId} companyName={companyName}
                onCompanyChange={(id, name) => { setCompanyId(id); setCompanyName(name || ''); }} />
            </TabsContent>

            <TabsContent value="expertise">
              <ExpertiseTab profile={profile} set={set} handleSave={handleSave} />
            </TabsContent>

            <TabsContent value="networking">
              <NetworkingTab profile={profile} set={set} handleSave={handleSave} />
            </TabsContent>

            <TabsContent value="settings">
              <SettingsTab user={user} notifPrefs={notifPrefs} setNotifPrefs={setNotifPrefs} handleSave={handleSave} securityRef={securityRef} privacyPrefs={privacyPrefs} setPrivacyPrefs={setPrivacyPrefs} />
            </TabsContent>
          </Tabs>
        </div>
      </main>
      <HeadshotCropper
        file={pendingPhoto}
        open={!!pendingPhoto}
        onClose={() => setPendingPhoto(null)}
        onSave={handleCroppedSave}
      />
    </div>
  );
}
